import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  FilePenLine,
  Download,
  Trash2,
} from "lucide-react"; // Removed FilterX, Papa, FileSaver
import constant from "../../constant";
import { Helmet } from "react-helmet";
// import { f } from "html2pdf.js"; // Removed - seems unused


const PatientDetails = ({ title }) => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("basic"); // Default tab

  // Sales state remains here
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);

  const userRole = localStorage.getItem("role");
  const adminSelectedUser = localStorage.getItem("adminSelectedUser");


  // Fetch device Details Effect (Unchanged)
  useEffect(() => {
    const fetchDeviceDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found");
          navigate("/login");
          return;
        }

        const response = await fetch(
          `${constant.REACT_APP_API_URL}/device/${deviceId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch device details");
        }

        const result = await response.json();
        setDevice(result.data[0]);
      } catch (error) {
        setError(error.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceDetails();
  }, [deviceId, navigate]);

  const constructApiUrl = (endpoint) => {
    const userRole = localStorage.getItem("role");
    const adminSelectedUser = localStorage.getItem("adminSelectedUser");
    let apiUrl = `${constant.REACT_APP_API_URL}${endpoint}`;
    if (userRole === "admin" && adminSelectedUser) {
      apiUrl = `${constant.REACT_APP_API_URL}${endpoint}${endpoint.includes("?") ? "&" : "?"
        }userId=${adminSelectedUser}`;
    }
    return apiUrl;
  };

  // Fetch Sales Data Effect (Unchanged)
  useEffect(() => {
    const fetchSalesData = async () => {
      if (activeTab === "sales") {
        setSalesLoading(true);
        setSalesError(null);
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            alert("No authentication token found");
            navigate("/login");
            return;
          }

          let apiUrl = constructApiUrl("/sales?deviceId=${deviceId}");

          const response = await fetch(apiUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error("Failed to fetch sales data");
          }

          const result = await response.json();
          setSalesData(result.data.salesData);
        } catch (error) {
          setSalesError(
            error.message || "An error occurred while fetching sales data"
          );
        } finally {
          setSalesLoading(false);
        }
      }
    };

    fetchSalesData();
  }, [activeTab, deviceId, navigate, userRole, adminSelectedUser]);



  // Sales Modal/Delete Logic (Unchanged)
  const openModal = (saleId) => {
    setSaleToDelete(saleId);
    setShowModal(true);
  };

  const closeModal = () => {
    setSaleToDelete(null);
    setShowModal(false);
  };

  const handleDelete = async () => {
    if (!saleToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/sale/${saleToDelete}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: false }), // Assuming soft delete
        }
      );

      if (response.ok) {
        // Refetch sales data or filter locally
        setSalesData((prevSales) =>
          prevSales.filter((sale) => sale._id !== saleToDelete)
        );
        alert("Sale deactivated successfully."); // Add user feedback
      } else {
        const result = await response.json();
        throw new Error(result.message || "Failed to deactivate sale");
      }
    } catch (error) {
      console.error("Error deactivating sale:", error);
      alert(`Error: ${error.message}`);
    } finally {
      closeModal();
    }
  };


  // Loading/Error States for Device Details (Unchanged)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  // --- JSX ---
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-10 pt-20 md:pt-10">
        <div className="max-w-7xl mx-auto p-6 bg-white rounded-xl shadow-lg">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate("/devices")}
                className="text-gray-500 hover:text-gray-700 transition-colors mr-1"
                aria-label="Back to devices"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-md font-bold text-gray-800 uppercase">
                {device?.name || "Device Details"}
              </h2>
            </div>

            {userRole === 'admin' ? (
              ''
            ) : (
              <button
                onClick={() => navigate(`/devices/edit/${deviceId}`)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-150"
              >
                <FilePenLine size={18} className="mr-2" />
                Edit
              </button>
            )}

          </div>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex flex-wrap -mb-px">
              {/* Tabs Definition */}
              {[
                { key: "basic", label: "Basic Information" },
                { key: "address", label: "Address Details" },
                { key: "sales", label: "Sales" },
                { key: "invoice", label: "Purchases" }
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap px-6 py-4 text-sm font-medium mr-2 mb-0 border-b-2 ${activeTab === tab.key
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  aria-current={activeTab === tab.key ? "page" : undefined}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          {/* Tab Content */}
          <div>
            {/* Basic Information Tab (Unchanged) */}
            <div className={`${activeTab === "basic" ? "block" : "hidden"}`}>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Display Fields */}
                  {[
                    { label: "Name", value: device?.name },
                    { label: "Mobile", value: device?.mobile },
                    { label: "Email", value: device?.email },
                    { label: "Device Type", value: device?.deviceType },
                  ].map((field) => (
                    <div key={field.label}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                      </label>
                      <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 min-h-[40px]">
                        {field.value || "-"}
                      </p>
                    </div>
                  ))}
                  {/* Status */}
                  <div className="flex items-center space-x-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Status:
                    </label>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${device?.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                        }`}
                    >
                      {device?.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Address Details Tab (Unchanged) */}
            <div className={`${activeTab === "address" ? "block" : "hidden"} space-y-6`}>
              {/* Billing Address */}
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Billing Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { label: "Name", value: device?.billingAddress?.name },
                    {
                      label: "Street Address",
                      value: device?.billingAddress?.streetAddress,
                    },
                    { label: "City", value: device?.billingAddress?.city },
                    { label: "State", value: device?.billingAddress?.state },
                    { label: "Country", value: device?.billingAddress?.country },
                    { label: "Pincode", value: device?.billingAddress?.pincode },
                    { label: "GST No", value: device?.billingAddress?.gstNo },
                  ].map((field) => (
                    <div key={`billing-${field.label}`}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                      </label>
                      <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 min-h-[40px]">
                        {field.value || "-"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Shipping Address */}
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Shipping Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { label: "Name", value: device?.shippingAddress?.name },
                    {
                      label: "Street Address",
                      value: device?.shippingAddress?.streetAddress,
                    },
                    { label: "City", value: device?.shippingAddress?.city },
                    { label: "State", value: device?.shippingAddress?.state },
                    {
                      label: "Country",
                      value: device?.shippingAddress?.country,
                    },
                    {
                      label: "Pincode",
                      value: device?.shippingAddress?.pincode,
                    },
                    { label: "GST No", value: device?.shippingAddress?.gstNo },
                  ].map((field) => (
                    <div key={`shipping-${field.label}`}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                      </label>
                      <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 min-h-[40px]">
                        {field.value || "-"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sales Tab (Unchanged, but note the added delete button functionality) */}
            <div className={`${activeTab === "sales" ? "block" : "hidden"}`}>
              {salesLoading ? (
                <div className="min-h-[200px] flex items-center justify-center">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
                </div>
              ) : salesError ? (
                <div className="text-center py-10 text-red-500 bg-red-50 p-4 rounded border border-red-200">
                  {salesError}
                </div>
              ) : salesData && salesData.length > 0 ? (
                <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                  <div className="min-w-full overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-brand-600">
                        {/* Sales Table Header */}
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Inv Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Inv Number
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Device Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Mobile
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Due Date
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {salesData.map((sale) => {
                          const isDueDateOverdue =
                            sale.dueDate &&
                            new Date(sale.dueDate) < new Date() &&
                            !sale.fullyPaid; // Check fullyPaid status
                          return (
                            <tr
                              key={sale._id}
                              className="hover:bg-gray-50 transition-colors duration-150"
                            >
                              {/* Sales Table Row Data */}
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {sale.invoiceDate
                                  ? new Date(
                                    sale.invoiceDate
                                  ).toLocaleDateString()
                                  : "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`text-sm font-medium ${isDueDateOverdue
                                    ? "text-red-600"
                                    : "text-gray-900"
                                    }`}
                                >
                                  {sale.invoiceNumber || "N/A"}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                                {sale.deviceData?.name || device?.name || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {sale.deviceData?.mobile || device?.mobile || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                                {sale.totalAmount?.toFixed(2) || "0.00"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {sale.dueDate
                                  ? new Date(sale.dueDate).toLocaleDateString()
                                  : "N/A"}
                                {isDueDateOverdue && (
                                  <div className="text-red-500 mt-1 text-xs font-semibold">
                                    Overdue
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                {/* Updated Status Logic */}
                                {sale.fullyPaid ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Paid
                                  </span>
                                ) : sale.amountReceived > 0 ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Partially Paid
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Unpaid
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                <div className="flex items-center justify-center space-x-3">
                                  <Link
                                    to={`/invoice/${sale._id}`}
                                    className="text-blue-600 hover:text-blue-800 transition-colors duration-150"
                                    title="View/Download Invoice"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Download size={18} />
                                  </Link>
                                  {/* Delete Button */}
                                  <button
                                    onClick={() => openModal(sale._id)}
                                    className="text-red-600 hover:text-red-800 transition-colors duration-150"
                                    title="Delete Sale"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-10">
                  <p>No sales invoices found for this device.</p>
                </div>
              )}
            </div>


            {/* Purchases Tab */}
            <div className={`${activeTab === "invoice" ? "block" : "hidden"}`}>
              <div className="text-center text-gray-500 py-10">
                <p>Purchase invoice functionality is not yet implemented.</p>
              </div>
            </div>


          </div> {/* End Tab Content */}
        </div> {/* End Max Width Container */}
      </div> {/* End Main BG Div */}

      {/* Confirmation Modal (Unchanged) */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 transition-opacity duration-300 ease-out"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl transform transition-all duration-300 ease-out scale-95 opacity-0 animate-modal-pop-in">
            {/* Modal Content */}
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
              <Trash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>
            <h3
              id="modal-title"
              className="text-lg font-semibold text-gray-900 text-center mb-2"
            >
              Confirm Deactivation
            </h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              Are you sure you want to deactivate this sale record? It will no longer be active but can potentially be restored later.
            </p>
            <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete} // Changed label for clarity
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientDetails;